import {
  Col,
  EditButton,
  Form,
  getDefaultSortOrder,
  Icons,
  Input,
  List,
  Row,
  Space,
  Table,
  TagField,
  TextField,
  useTable,
} from "@pankod/refine-antd";
import { CrudFilters, IResourceComponentsProps } from "@pankod/refine-core";
import type { CheckboxValueType } from "antd/es/checkbox/Group";
import type { ColumnsType } from "antd/es/table";
import { ButtonColumn } from "components/buttonColumn";
import { ButtonExport } from "components/buttonExport";
import { ButtonFilter } from "components/buttonFilter";
import useMediaScreen from "hooks/useMediaScreen";
import React, { useState } from "react";
import { ITag } from "types/tag.interface";
import { ITrack, ITrackResponse } from "types/track.interface";
import { formatDateTime, formatTime, getOrderNumber, includesClassName } from "utils";

export const DraftTracksList: React.FC<IResourceComponentsProps> = () => {
  const { isMobile } = useMediaScreen();
  const { tableProps, sorter, searchFormProps }: any = useTable<ITrackResponse>(
    {
      resource: "tracks",
      initialPageSize: 100,
      metaData: [
        {
          field: "isDraft",
          value: true,
        },
      ],
      onSearch: (params) => {
        const filters: CrudFilters = [];
        const input = params;
        filters.push({
          field: "search",
          operator: "in",
          value: input,
        });
        return filters;
      },
    }
  );

  const current = tableProps?.pagination?.current;
  const pageSize = tableProps?.pagination?.pageSize;

  const defaultColumn: CheckboxValueType[] = [
    "title",
    "artist",
    "user",
    "price",
    "createdAt",
  ];

  const [hideCols, setHideCols] = useState<CheckboxValueType[]>(defaultColumn);

  const options = [
    { label: "Title", value: "title" },
    { label: "Artist", value: "artist" },
    { label: "Owner", value: "user" },
    { label: "Price", value: "price" },
    { label: "Created", value: "createdAt" },
    { label: "Duration", value: "duration" },
    { label: "Genre", value: "genre" },
    { label: "Label", value: "label" },
    { label: "Tags", value: "tags" },
    { label: "TrackKey", value: "trackKey" },
    { label: "Type", value: "type" },
    { label: "Year", value: "year" },
  ];

  const columns: ColumnsType<ITrack> = [
    {
      title: "Index",
      dataIndex: "_id",
      key: "_id",
      render: (value, item, index) => (
        <TextField value={getOrderNumber(current, pageSize, index)} />
      ),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (value) => <TextField value={value} />,
      className: includesClassName(hideCols, "title"),
      defaultSortOrder: getDefaultSortOrder("title", sorter),
      sorter,
    },
    {
      title: "Artist",
      dataIndex: "artist",
      key: "artist",
      render: (value) => <TextField value={value} />,
      className: includesClassName(hideCols, "artist"),
      defaultSortOrder: getDefaultSortOrder("artist", sorter),
      sorter,
    },
    {
      title: "Owner",
      dataIndex: "user",
      key: "user",
      render: (value) => <TextField value={value?.username} />,
      className: includesClassName(hideCols, "user"),
      defaultSortOrder: getDefaultSortOrder("user", sorter),
      sorter,
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      render: (value) => <TextField value={value} />,
      className: includesClassName(hideCols, "price"),
      defaultSortOrder: getDefaultSortOrder("price", sorter),
      sorter,
    },
    {
      title: "createdAt",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (value) => <TextField value={formatDateTime(value)} />,
      className: includesClassName(hideCols, "createdAt"),
      defaultSortOrder: getDefaultSortOrder("createdAt", sorter),
      sorter,
    },
    {
      title: "Duration",
      dataIndex: "duration",
      key: "duration",
      render: (value) => <TextField value={formatTime(value || 0)} />,
      className: includesClassName(hideCols, "duration"),
      defaultSortOrder: getDefaultSortOrder("duration", sorter),
      sorter,
    },
    {
      title: "Genre",
      dataIndex: "genre",
      key: "genre",
      render: (value) => <TextField value={value?.name} />,
      className: includesClassName(hideCols, "genre"),
      defaultSortOrder: getDefaultSortOrder("genre", sorter),
      sorter,
    },
    {
      title: "Label",
      dataIndex: "label",
      key: "label",
      render: (value) => <TextField value={value?.name} />,
      className: includesClassName(hideCols, "label"),
      defaultSortOrder: getDefaultSortOrder("label", sorter),
      sorter,
    },
    {
      title: "tags",
      dataIndex: "tags",
      key: "tags",
      render: (value) => (
        <div>
          {value.map((item: ITag) => (
            <TagField value={item?.name} />
          ))}
        </div>
      ),
      className: includesClassName(hideCols, "tags"),
      defaultSortOrder: getDefaultSortOrder("tags", sorter),
      sorter,
    },
    {
      title: "TrackKey",
      dataIndex: "trackKey",
      key: "trackKey",
      render: (value) => <TextField value={value?.musicKey} />,
      className: includesClassName(hideCols, "trackKey"),
      defaultSortOrder: getDefaultSortOrder("trackKey", sorter),
      sorter,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (value) => <TextField value={value} />,
      className: includesClassName(hideCols, "type"),
      defaultSortOrder: getDefaultSortOrder("type", sorter),
      sorter,
    },
    {
      title: "Year",
      dataIndex: "year",
      key: "year",
      render: (value) => <TextField value={value} />,
      className: includesClassName(hideCols, "year"),
      defaultSortOrder: getDefaultSortOrder("year", sorter),
      sorter,
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (_, record) => (
        <Space>
          <EditButton hideText size="small" recordItemId={record._id} />
          {/* <DeleteButton hideText size='small' recordItemId={record._id} /> */}
        </Space>
      ),
    },
  ];

  return (
      <List>
          <Row style={{ display: 'block' }}>
              <div style={{ display: 'flex', gap: '5px', marginBottom: '5px' }}>
                  <TextField value='Total:' style={{ color: '#626262', fontSize: '14px', fontWeight: 700 }} />
                  <TextField value={tableProps?.pagination?.total} style={{ color: '#626262', fontSize: '14px', fontWeight: 700 }} />
              </div>
              <Col>
                  <Form layout='vertical' {...searchFormProps}>
                      <Form.Item label='Search' name='search' style={{ width: isMobile? '100%' :'50%' }} initialValue=''>
                          <Input placeholder='Title, artist' prefix={<Icons.SearchOutlined />} />
                      </Form.Item>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Form.Item>
                              <ButtonFilter />
                          </Form.Item>
                          <div>
                              <ButtonColumn options={options} setHideCols={setHideCols} defaultColumn={defaultColumn} />
                              <ButtonExport path='tracks' />
                          </div>
                      </div>
                  </Form>
              </Col>
              <Col>
                  <Table
                      {...tableProps}
                      pagination={{
                          ...tableProps?.pagination,
                          showSizeChanger: true,
                      }}
                      columns={columns}
                      rowKey='id'
                  />
              </Col>
          </Row>
      </List>
  );
};
